import { useFlags } from "launchdarkly-react-client-sdk";
import React, { createContext, useCallback, useEffect, useRef, useState } from "react";

import type { Client } from "@storyboard/web-client";
import { type FlagData, flags as GlobalFlags, notifyFlagChange } from "@/config";
import PubSub from "@/PubSub";

export type Config = {
  baseDomain: string;
  graphqlApiKey: string;
  graphqlUrl: string;
  sessionRecordingLength: number;
  tusUrl: string;
};

type AppState = {
  audioCtx?: AudioContext;
  config?: Config;
  flags: FlagData;
  pubSub?: PubSub;
  redirectPath?: string;
  setFlags: (overrides: Partial<FlagData>) => void;
  clearFlagOverrides: () => void;
  setRedirectPath: (path: string) => void;
  tracking?: any;
};

export const AppContext = createContext<AppState>({
  setRedirectPath: () => {},
  flags: {},
  setFlags: (_) => {},
  clearFlagOverrides: () => {},
});

type Props = {
  client: Client;
  baseDomain?: string;
  children: React.ReactNode | React.ReactNode[];
  graphqlApiKey: string;
  graphqlUrl: string;
  sessionRecordingLength: number;
  tusUrl: string;
  initialFlags: FlagData;
};

const AppStateProvider = ({
  client,
  baseDomain,
  tusUrl,
  graphqlUrl,
  graphqlApiKey,
  children,
  sessionRecordingLength,
  initialFlags,
}: Props) => {
  const [flags, setFlagsState] = useState<FlagData>(initialFlags);
  const [redirectPath, setRedirectPath] = useState<string>("");
  const [errorCodeDialog, setErrorCodeDialog] = useState<boolean>(false);
  const [errorCount, setErrorCount] = useState<number>(0);
  const debugOverrides = useRef<Set<string>>(new Set());
  const ldFlags = useFlags();

  const setFlags = useCallback((overrides: Partial<FlagData>) => {
    for (const key of Object.keys(overrides)) {
      debugOverrides.current.add(key);
    }
    setFlagsState((prev) => ({ ...prev, ...overrides }));
  }, []);

  const clearFlagOverrides = useCallback(() => {
    debugOverrides.current.clear();
    setFlagsState((prev) => ({ ...prev, ...ldFlags }));
  }, [ldFlags]);

  useEffect(() => {
    const merged: FlagData = { ...ldFlags };
    for (const key of debugOverrides.current) {
      delete merged[key];
    }
    setFlagsState((prev) => ({ ...prev, ...merged }));
  }, [ldFlags]);

  useEffect(() => {
    for (const flag in flags) {
      GlobalFlags[flag] = flags[flag];
    }
    notifyFlagChange();
  }, [flags]);

  const clearErrors = () => {
    setErrorCodeDialog(() => false);
    setErrorCount(() => 0);
  };

  const handleErrorCodes = (error: any) => {
    setErrorCount((value) => value + 1);
    const findString = "net::ERR";
    if (error?.message?.includes(findString) || error?.stack?.includes(findString)) {
      setErrorCodeDialog(() => true);
    } else if (errorCount >= 10 && !errorCodeDialog) {
      setErrorCodeDialog(() => true);
    }
  };
  client.on("success", () => clearErrors());
  client.on("errorCodes", (errors) => handleErrorCodes(errors));

  const pubSub = React.useMemo(() => new PubSub(graphqlUrl, graphqlApiKey), [graphqlUrl, graphqlApiKey]);

  const appState: AppState = {
    redirectPath,
    setRedirectPath,
    flags,
    setFlags,
    clearFlagOverrides,
    pubSub,
    config: {
      baseDomain,
      graphqlApiKey,
      graphqlUrl,
      sessionRecordingLength,
      tusUrl,
    },
  };

  return <AppContext.Provider value={appState}>{children}</AppContext.Provider>;
};
export default AppStateProvider;
